import Animated, {
  Easing,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import ClearIcon from '@assets/icons/clear.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import validator from 'validator';
import {
  TextInput as BaseTextInput,
  ColorValue,
  CursorValue,
  Keyboard,
  PixelRatio,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { Button } from './button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { InputAccessoryViewSafe } from './input-accessory-view-safe';

const DONE_BUTTON_ACCESSORY_VIEW_ID = 'done-button-accessory-view';

export enum Validations {
  DECIMAL = 'DECIMAL',
  INTEGER = 'INTEGER',
  POSITIVE = 'POSITIVE',
}

type ValidationFn = (input: string) => boolean;

export const createMaxDecimalDigitsValidator =
  (minDigits: number, maxDigits: number) => (input: string) =>
    validator.isDecimal(input, { decimal_digits: `${minDigits},${maxDigits}` });

export const createMaxFloatValidator = (max: number) => (input: string) =>
  validator.isFloat(input, { max });

export const createMinFloatValidator = (min: number) => (input: string) =>
  validator.isFloat(input, { min });

export const validationFunctions: Record<Validations, ValidationFn> = {
  [Validations.DECIMAL]: (input: string) => validator.isFloat(input),
  [Validations.INTEGER]: (input: string) => validator.isInt(input),
  [Validations.POSITIVE]: (input: string) =>
    validator.isFloat(input, { min: 0 }),
};

export type Props = TextInputProps & {
  caption?: string | null;
  captionStyle?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  disabledStyle?: StyleProp<TextStyle>;
  error?: string | null;
  focusedBorderColor?: string;
  inDraggableSheet?: boolean;
  inputContainerStyle?: StyleProp<ViewStyle>;
  inputRef?: React.RefObject<BaseTextInput | null>;
  label?: string | null;
  labelStyle?: StyleProp<TextStyle>;
  leftElement?: React.ReactNode;
  leftElementBordered?: boolean;
  leftElementStyle?: StyleProp<ViewStyle>;
  maxLength?: number;
  onPress?: () => void;
  placeholderTextColor?: ColorValue;
  rightElement?: React.ReactNode;
  rightElementBordered?: boolean;
  rightElementStyle?: StyleProp<ViewStyle>;
  showDoneButtonAccessoryView?: boolean;
  useOuterLabel?: boolean;
  validators?: ValidationFn[];
  // TextInput is controlled to prevent re-rendering when label is applied on Android
  value: string;
};

const CLEAR_HITSLOP = {
  bottom: 16,
  left: 16,
  right: 16,
  top: 16,
};

export const INPUT_HEIGHT = 20 * PixelRatio.getFontScale();
export const LABEL_FONT_SIZE = 10;

export const TextInput: React.FC<Props> = ({
  caption,
  captionStyle,
  clearButtonMode,
  containerStyle,
  editable = true,
  error,
  focusedBorderColor,
  hitSlop,
  inDraggableSheet = false,
  inputAccessoryViewID,
  inputContainerStyle,
  inputRef,
  label,
  placeholderTextColor,
  leftElement,
  leftElementBordered,
  leftElementStyle,
  maxLength,
  onBlur,
  onChangeText,
  onPress,
  onFocus,
  onKeyPress,
  useOuterLabel,
  pointerEvents = 'auto',
  rightElement,
  rightElementBordered,
  rightElementStyle,
  selectTextOnFocus,
  showDoneButtonAccessoryView,
  style,
  validators = [],
  value,
  labelStyle,
  disabledStyle,
  ...rest
}) => {
  const [styles, theme] = useStyles();

  const focusAnimatedValue = useSharedValue(0);
  const baseInput = useRef<BaseTextInput>(null);
  const [isFocused, setIsFocused] = useState(false);

  const focusInput = () => {
    baseInput.current?.focus();
  };

  const renderClearButton =
    clearButtonMode && clearButtonMode !== 'never' && value;

  useEffect(() => {
    focusAnimatedValue.value = withTiming(isFocused ? 1 : 0, {
      duration: 250,
      easing: Easing.linear,
    });
  }, [focusAnimatedValue, isFocused]);

  useEffect(() => {
    if (Platform.OS === 'web' && rest.autoFocus) {
      focusInput();
    }
    // Run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flattenedContainerStyle = containerStyle
    ? StyleSheet.flatten(containerStyle)
    : {};
  const flattenedBorderColor =
    (flattenedContainerStyle.borderColor as string) || theme.colors.white;

  const doneButtonAccessoryViewID =
    // InputAccessoryView is only supported on iOS
    Platform.OS === 'ios' && showDoneButtonAccessoryView
      ? DONE_BUTTON_ACCESSORY_VIEW_ID
      : undefined;

  focusedBorderColor = focusedBorderColor || theme.colors.accent;

  const TextInputComponent = inDraggableSheet
    ? BottomSheetTextInput
    : BaseTextInput;

  const animatedContainerStyle = useAnimatedStyle(
    () => ({
      borderColor: interpolateColor(
        focusAnimatedValue.value,
        [0, 1],
        error
          ? [theme.colors.negative, theme.colors.negative]
          : [flattenedBorderColor, focusedBorderColor!],
      ),
    }),
    [
      focusAnimatedValue,
      error,
      theme,
      flattenedBorderColor,
      focusedBorderColor,
    ],
  );

  const handlePress = editable ? focusInput : onPress;
  // When pointerEvents is 'none', we should disable the TouchableWithoutFeedback
  // so the parent component can handle the touch events
  const shouldDisableTouchable =
    (!editable && !onPress) || pointerEvents === 'none';

  // don't allow outer touchable to be focusable on web, tabbing goes directly
  // to the text input
  return (
    <>
      {useOuterLabel && label ? (
        <Text
          onPress={editable ? focusInput : onPress}
          style={[
            styles.label,
            styles.outerLabel,
            editable
              ? undefined
              : disabledStyle
                ? disabledStyle
                : styles.disabled,
            labelStyle,
          ]}
          testID="outer-label"
        >
          {label}
        </Text>
      ) : null}
      <TouchableWithoutFeedback
        disabled={shouldDisableTouchable}
        focusable={false}
        hitSlop={hitSlop}
        onPress={handlePress}
      >
        <Animated.View
          style={[
            styles.base,
            flattenedContainerStyle,
            animatedContainerStyle,
            editable ? undefined : styles.baseDisabled,
          ]}
        >
          {leftElement ? (
            <View
              style={[
                styles.elementContainer,
                styles.leftElementContainer,
                leftElementBordered ? styles.leftElementWithBorder : null,
                leftElementStyle,
              ]}
            >
              {leftElement}
            </View>
          ) : null}
          <View
            pointerEvents={editable ? pointerEvents : 'none'}
            style={[styles.defaultInputContainer, inputContainerStyle]}
          >
            {!useOuterLabel && label ? (
              <Text
                onPress={editable ? focusInput : onPress}
                style={[
                  styles.label,
                  editable
                    ? undefined
                    : disabledStyle
                      ? disabledStyle
                      : styles.disabled,
                ]}
                testID="label"
              >
                {label}
              </Text>
            ) : null}
            <TextInputComponent
              editable={editable}
              inputAccessoryViewID={
                inputAccessoryViewID || doneButtonAccessoryViewID
              }
              onBlur={(e) => {
                if (!editable) {
                  return;
                }
                setIsFocused(false);
                onBlur?.(e);
              }}
              onChangeText={(text) => {
                const isValid =
                  text === '' || validators.every((fn) => fn(text));
                if (maxLength) {
                  if (isValid && text.length <= maxLength) {
                    onChangeText?.(text);
                  }
                } else if (isValid) {
                  onChangeText?.(text);
                }
              }}
              onFocus={(e) => {
                if (!editable) {
                  return;
                }
                setIsFocused(true);
                onFocus?.(e);
              }}
              onKeyPress={(e) => {
                if (!editable) {
                  return;
                }
                onKeyPress?.(e);
              }}
              onPress={onPress}
              placeholderTextColor={
                placeholderTextColor ?? theme.sk.color.text.hint
              }
              ref={(ref) => {
                // @ts-ignore
                baseInput.current = ref;

                if (inputRef) {
                  // @ts-ignore
                  inputRef.current = ref;
                }
              }}
              selectionColor={theme.colors.accent}
              selectTextOnFocus={selectTextOnFocus}
              style={[
                styles.input,
                editable
                  ? undefined
                  : disabledStyle
                    ? disabledStyle
                    : styles.disabled,
                style,
              ]}
              value={value}
              {...rest}
            />
          </View>
          {doneButtonAccessoryViewID ? (
            <InputAccessoryViewSafe nativeID={doneButtonAccessoryViewID}>
              <View
                pointerEvents="box-none"
                style={styles.inputAccessoryContent}
              >
                <Button
                  onPress={Keyboard.dismiss}
                  trackingData={null}
                  type="tertiary"
                >
                  {i18n.t('done')}
                </Button>
              </View>
            </InputAccessoryViewSafe>
          ) : null}
          {renderClearButton ? (
            <View
              style={[styles.elementContainer, styles.rightElementContainer]}
            >
              <TouchableOpacity
                hitSlop={CLEAR_HITSLOP}
                onPress={() => {
                  onChangeText?.('');
                  baseInput.current?.focus();
                }}
                testID="clear-button"
              >
                <ClearIcon fill={theme.colors.primary} />
              </TouchableOpacity>
            </View>
          ) : null}
          {rightElement ? (
            <View
              style={[
                styles.elementContainer,
                styles.rightElementContainer,
                rightElementBordered ? styles.rightElementWithBorder : null,
                rightElementStyle,
              ]}
            >
              {rightElement}
            </View>
          ) : null}
        </Animated.View>
      </TouchableWithoutFeedback>
      {error ? (
        <Text
          style={[TYPOGRAPHY.xsmall, styles.inputCaption, styles.errorText]}
        >
          {error}
        </Text>
      ) : maxLength ? (
        <Text
          style={[TYPOGRAPHY.xsmall, styles.inputCaption, styles.maxLengthText]}
        >
          {`${value.length} / ${maxLength}`}
        </Text>
      ) : caption ? (
        <Text
          style={[
            TYPOGRAPHY.xsmall,
            styles.inputCaption,
            styles.captionText,
            captionStyle,
          ]}
        >
          {caption}
        </Text>
      ) : null}
    </>
  );
};

export const TEXT_INPUT_HEIGHT = 56;

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    height: TEXT_INPUT_HEIGHT,
    ...Platform.select({
      web: {
        cursor: 'text' as CursorValue,
      },
    }),
  },
  baseDisabled: {
    ...Platform.select({
      web: {
        cursor: 'default' as CursorValue,
      },
    }),
  },
  captionText: {
    color: theme.colors.inactiveText,
  },
  defaultInputContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  disabled: {
    color: theme.colors.inactiveText,
  },
  elementContainer: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  errorText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.negative,
  },
  input: {
    ...FONTS.primary,
    color: theme.sk.color.text.primary,
    fontSize: 16,
    paddingHorizontal: 16,
    // Removes React Native default multiline padding
    paddingTop: 0,
    ...Platform.select({
      // Set matching bottom padding on Android for vertical alignment
      android: { paddingBottom: 0 },
      // Declaring this property on mobile breaks the app
      web: { outlineWidth: 0 },
    }),
  },
  inputAccessoryContent: {
    alignItems: 'flex-end',
    backgroundColor: theme.colors.white,
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  inputCaption: {
    marginTop: 4,
  },
  label: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: LABEL_FONT_SIZE,
    lineHeight: LABEL_FONT_SIZE + 2,
    marginBottom: 2,
    paddingHorizontal: 16,
  },
  leftElementContainer: {
    paddingLeft: 16,
  },
  leftElementWithBorder: {
    borderColor: theme.colors.stroke,
    borderRightWidth: StyleSheet.hairlineWidth,
    paddingRight: 16,
  },
  maxLengthText: {
    textAlign: 'right',
  },
  outerLabel: {
    fontSize: 12,
    lineHeight: 14,
    marginBottom: 8,
    paddingHorizontal: 0,
  },
  rightElementContainer: {
    paddingRight: 16,
  },
  rightElementWithBorder: {
    borderColor: theme.colors.stroke,
    borderLeftWidth: StyleSheet.hairlineWidth,
    paddingLeft: 16,
  },
}));
